// src/components/settings/ColoursTab.tsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Alert, Table, TableRow } from '@mui/material';
import { referenceDataAPI, PetType, PetColour } from '../../services/api';
import { useErrorHandler } from '../../errors';
import { useGlobalToast } from '../../App';
import {
  ConfirmDeleteDialog,
  RefCountChip,
  SystemChip,
} from './shared/SettingsElements';
import SettingsHeaderRow from './shared/SettingsHeaderRow';
import LoadingSpinner from '../common/LoadingSpinner';
import SettingsTableActions from './shared/SettingsTableActions';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeaderCell,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledTableChipCell,
  StyledTableChipCellContent,
  StyledTableActionsCell,
} from './shared/SettingsTableElements';
import PetColourDialog from './shared/dialogs/PetColourDialog';
import SimilarityDialog from '../forms/shared/SimilarityDialog';
import { checkReferenceDataSimilarity } from '../../utils/referenceDataSimilarityHelper';

interface ColoursTabProps {
  usage: Record<number, number>;
  onDataChanged: () => void;
}

const ColoursTab: React.FC<ColoursTabProps> = ({ usage, onDataChanged }) => {
  const [items, setItems] = useState<PetColour[]>([]);
  const [petTypes, setPetTypes] = useState<PetType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PetColour | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [searchText, setSearchText] = useState('');
  const [showSimilarityDialog, setShowSimilarityDialog] = useState(false);
  const [similarItem, setSimilarItem] = useState<PetColour | null>(null);
  const [pendingSave, setPendingSave] = useState<{
    colour: string;
    petTypeId: number;
    active?: boolean;
  } | null>(null);
  const { handleError } = useErrorHandler();
  const { showSuccess, showError } = useGlobalToast();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [colours, types] = await Promise.all([
        referenceDataAPI.getPetColours(),
        referenceDataAPI.getPetTypes(),
      ]);
      setItems(colours);
      setPetTypes(types);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    if (!searchText) return items;
    const lower = searchText.toLowerCase();
    return items.filter(
      (i) =>
        i.colour.toLowerCase().includes(lower) ||
        (i as any).petTypeNavigation?.type?.toLowerCase().includes(lower)
    );
  }, [items, searchText]);

  const openDialog = (item?: PetColour) => {
    setEditing(item ?? null);
    setDialogOpen(true);
  };

  const proceedWithSave = async (formData: {
    colour: string;
    petTypeId: number;
    active?: boolean;
  }) => {
    try {
      if (editing) {
        await referenceDataAPI.updatePetColour(editing.id, {
          colour: formData.colour,
          petTypeId: formData.petTypeId,
          active: formData.active,
        });
        showSuccess();
      } else {
        await referenceDataAPI.createPetColour({
          colour: formData.colour,
          petTypeId: formData.petTypeId,
          active: formData.active,
        });
        showSuccess();
      }
      await fetchData();
      onDataChanged();
      setDialogOpen(false);
    } catch (e) {
      handleError(e);
    }
  };

  const save = async (formData: {
    colour: string;
    petTypeId: number;
    active?: boolean;
  }) => {
    const scopedItems = items.filter((i) => i.petType === formData.petTypeId);
    const { isExactMatch, match } = checkReferenceDataSimilarity(
      formData.colour,
      scopedItems,
      (i) => i.colour,
      (i) => i.id,
      editing?.id
    );

    if (isExactMatch) {
      showError('A colour with this name already exists for this pet type');
      return;
    }

    if (match) {
      setSimilarItem(match);
      setPendingSave(formData);
      setShowSimilarityDialog(true);
      return;
    }

    await proceedWithSave(formData);
  };

  const handleUseExisting = () => {
    setShowSimilarityDialog(false);
    setSimilarItem(null);
    setPendingSave(null);
    setDialogOpen(false);
  };

  const handleCreateAnyway = async () => {
    setShowSimilarityDialog(false);
    setSimilarItem(null);
    if (pendingSave) {
      await proceedWithSave(pendingSave);
    }
    setPendingSave(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await referenceDataAPI.deletePetColour(deleteTarget.id);
      showSuccess();
      await fetchData();
      onDataChanged();
    } catch (e) {
      handleError(e);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) return <LoadingSpinner message="Loading Colours..." size={80} />;

  return (
    <Box>
      <SettingsHeaderRow
        title="Colours"
        onAdd={() => openDialog()}
        addLabel="Add Colour"
        buttonMinWidth="254px"
        onSearch={setSearchText}
        onClearSearch={() => setSearchText('')}
        searchText={searchText}
        searchPlaceholder="Search colours..."
      />
      {items.length === 0 ? (
        <Alert severity="info">No colours found.</Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No colours match "{searchText}".</Alert>
      ) : (
        <StyledTableContainer>
          <Table size="small">
            <StyledTableHead>
              <TableRow>
                <StyledTableHeaderCell>Colour</StyledTableHeaderCell>
                <StyledTableHeaderCell>Type</StyledTableHeaderCell>
                <StyledTableHeaderCell align="center">
                  Refs
                </StyledTableHeaderCell>
                <StyledTableHeaderCell align="right">
                  Actions
                </StyledTableHeaderCell>
              </TableRow>
            </StyledTableHead>
            <StyledTableBody>
              {filtered.map((item, index) => {
                const refCount = usage[item.id] ?? 0;
                return (
                  <StyledTableRow key={item.id} isOdd={index % 2 === 0}>
                    <StyledTableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography>{item.colour}</Typography>
                        {item.isSystem && <SystemChip />}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography>
                        {(item as any).petTypeNavigation?.type ?? '-'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableChipCell>
                      <StyledTableChipCellContent>
                        <RefCountChip count={refCount} />
                      </StyledTableChipCellContent>
                    </StyledTableChipCell>
                    <StyledTableActionsCell align="right">
                      <SettingsTableActions
                        itemId={item.id}
                        itemName={item.colour}
                        refCount={refCount}
                        isSystem={item.isSystem}
                        onEdit={() => openDialog(item)}
                        onDelete={() =>
                          setDeleteTarget({ id: item.id, name: item.colour })
                        }
                        refLabel="colour"
                      />
                    </StyledTableActionsCell>
                  </StyledTableRow>
                );
              })}
            </StyledTableBody>
          </Table>
        </StyledTableContainer>
      )}

      <PetColourDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={save}
        editing={editing}
        petTypes={petTypes}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title="Delete colour?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <SimilarityDialog
        open={showSimilarityDialog}
        entityType="petColour"
        similarEntity={similarItem}
        isEditMode={!!editing}
        onUseExisting={handleUseExisting}
        onCreateNew={handleCreateAnyway}
        onClose={() => setShowSimilarityDialog(false)}
      />
    </Box>
  );
};

export default ColoursTab;
